// The note-sync mutation's data core — the W3c split of
// `src/hooks/mutation/useMutateUpdateNoteBySessionId.ts` (D4). The old path
// keeps a signature-stable delegate that resolves `useEnableBlockEditorV1()`
// (a heavy Athena/integrations composition) and passes the boolean in; the
// body below is otherwise byte-identical. Keys + cache types come from
// contracts, the api fn from the package carve. Severed 2026-08-05
// (ARCFE-921, ratified mechanism: app reaction at the delegate): the error
// toast crosses the `onSyncError` callback, invoked exactly where the toast
// fired — after the optimistic rollback.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import { useState } from 'react';

import { logger, sanitizeError } from '@heidi/runtime/logger';
import { ContentType, type components } from '@heidi/schema/ml-scribe';

import {
  GET_CONSULT_NOTES,
  GET_DOCUMENTS_BY_ID,
  GET_SESSION_BY_ID,
  type QuerySessionResponse,
} from '../../contracts';
import { updateConsultNote } from '../api/notes';

type GetConsultNotesResponse = components['schemas']['GetConsultNotesResponse'];
type GetDocumentsResponse = components['schemas']['GetDocumentsResponse'];

type Props = {
  consultNote: string;
  sessionId: string;
  /** Marks an editor's autosave; reported through `trackAutosave` when wired. */
  autosave?: boolean;
};

/** Lengths only — the note itself is PHI and never crosses this seam. */
export type NoteSyncOutcomeInfo<TEditorType extends string> = {
  sessionId: string;
  contentType: ContentType;
  contentLength: number;
  editorType: TEditorType;
};

export const SYNC_NOTE_TO_SERVER = 'syncNoteToServer';

/** A payload carrying no authored characters — `''`, or whitespace only. */
export const isBlankNotePayload = (note: string | null | undefined): boolean =>
  (note ?? '').trim().length === 0;

/**
 * Persists consult-note edits to the server.
 *
 * @param options.contentTypeOverride - Bypass the `enableBlockEditorV1`-
 *   derived `content_type` and label the saved note with the caller-supplied
 *   type. Required for surfaces (e.g. Haiku FF-ON markdown editor) that
 *   always emit a specific format regardless of the user-cohort block-editor
 *   flag; without this, markdown payloads would be saved with `ContentType.HTML`
 *   when `release-block-editor-v1` is enabled, corrupting the chart write.
 * @param options.enableBlockEditorV1 - The old path's `useEnableBlockEditorV1()`
 *   read, delegate-resolved (W3c).
 * @param options.editorType - Which editor authored the payload, resolved
 *   app-side and treated here as an opaque token. Stamped on the failure log
 *   and both outcome ports so all three share an axis; `contentType` cannot
 *   serve that role because both note-editor twins write `markdown`.
 */
export function useMutateSyncNoteToServer<TEditorType extends string = string>(options: {
  contentTypeOverride?: ContentType;
  enableBlockEditorV1: boolean;
  editorType: TEditorType;
  /** App-reaction port: fired after the optimistic rollback (ARCFE-921). */
  onSyncError?: (error: Error) => void;
  /** App-reaction port: the denominator, fired once per dispatch. */
  onSyncAttempt?: (info: NoteSyncOutcomeInfo<TEditorType>) => void;
  /** App-reaction port: the numerator, fired once the server has committed. */
  onSyncSuccess?: (info: NoteSyncOutcomeInfo<TEditorType>) => void;
  /** App-telemetry port: wraps an autosave's save so it reports as one attempt. */
  trackAutosave?: <T>(
    info: NoteSyncOutcomeInfo<TEditorType>,
    save: () => Promise<T>
  ) => Promise<T>;
}) {
  const queryClient = useQueryClient();
  // `onMutate`'s snapshot, read back in `mutationFn` so the attempt lands in
  // the ports' cohort. Keyed by identity: each `mutate` needs fresh variables.
  const [outcomeByDispatch] = useState(
    () => new WeakMap<Props, NoteSyncOutcomeInfo<TEditorType>>()
  );

  const getSessionKey = GET_SESSION_BY_ID;

  const resolvedContentType =
    options?.contentTypeOverride ??
    (options.enableBlockEditorV1 ? ContentType.HTML : ContentType.MARKDOWN);

  return useMutation({
    mutationKey: [SYNC_NOTE_TO_SERVER],
    mutationFn: async (variables: Props) => {
      const { consultNote, sessionId } = variables;
      const save = async () => {
        const session = queryClient.getQueryData<QuerySessionResponse>([
          getSessionKey,
          { sessionId: sessionId },
        ]);

        if (!session) {
          throw new Error('Session not found');
        }

        return await updateConsultNote({
          request: {
            edited_note: consultNote,
            content_type: resolvedContentType,
          },
          sessionId: sessionId,
        });
      };

      const outcome = outcomeByDispatch.get(variables);
      return variables.autosave && outcome && options.trackAutosave
        ? options.trackAutosave(outcome, save)
        : save();
    },
    onMutate: async (variables) => {
      const { consultNote, sessionId } = variables;
      // Snapshotted whole rather than re-read at settle time: TanStack pushes
      // fresh options into an in-flight mutation, and the optimistic write
      // below flips the JSON gate the app resolves the label from — either
      // would land the two halves of one save in different cohorts.
      const outcome: NoteSyncOutcomeInfo<TEditorType> = {
        sessionId,
        contentType: resolvedContentType,
        contentLength: consultNote.length,
        editorType: options.editorType,
      };
      outcomeByDispatch.set(variables, outcome);

      // Ahead of every guard below, so the denominator also counts dispatches
      // that never reach the server.
      options.onSyncAttempt?.(outcome);

      await queryClient.cancelQueries({
        queryKey: [getSessionKey, { sessionId }],
      });

      const session = queryClient.getQueryData<QuerySessionResponse>([
        getSessionKey,
        { sessionId },
      ]);

      const previousNote = session?.consult_note?.result ?? '';
      if (isBlankNotePayload(consultNote) && !isBlankNotePayload(previousNote)) {
        // Detection only — the write still proceeds. Refusing it would block a
        // clinician deliberately clearing a note, trading a suspected silent
        // loss for a certain one. Counted at dispatch rather than on success: a
        // guard would block the attempt, and a drop after the server commits
        // lands in `onError`, so success would undercount real overwrites.
        // Lengths only: the note itself is PHI.
        logger.warn('Blank note payload dispatched over existing content', {
          sessionId,
          contentType: resolvedContentType,
          payloadLength: consultNote.length,
          previousLength: previousNote.length,
        });
      }

      // don't update the note if content is the same
      if (session?.consult_note?.result === consultNote) {
        return { previousSession: session, outcome };
      }

      // Check if documents exist for this session
      const existingDocuments = queryClient.getQueryData<GetDocumentsResponse>([
        GET_DOCUMENTS_BY_ID,
        { sessionId },
      ])?.documents;

      const hasDocuments = existingDocuments && existingDocuments.length > 0;

      queryClient.setQueryData<QuerySessionResponse>(
        [getSessionKey, { sessionId }],
        (oldData) => {
          if (!oldData) {
            return undefined;
          }

          return produce(oldData, (draft) => {
            if (draft.consult_note) {
              draft.consult_note.result = consultNote;
              draft.consult_note.content_type = resolvedContentType;
              draft.consult_note.updated_at = new Date().toISOString();
            }
            // Only show sync button if documents have been generated
            draft.shouldShowDocumentSyncChanges = hasDocuments || false;
          });
        }
      );

      return { previousSession: session, outcome };
    },
    onSuccess: (response, { sessionId }, context) => {
      // Ahead of the empty-body early return: the server committed either way,
      // and dropping the numerator there would depress the rate.
      if (context?.outcome) options.onSyncSuccess?.(context.outcome);

      const data = response?.data;
      if (!data) return;
      const updatedNote = data.consult_note;
      const consultNotesKey = [GET_CONSULT_NOTES, { sessionId }];
      const cached = queryClient.getQueryData<GetConsultNotesResponse>(consultNotesKey);
      const noteIndex =
        cached?.consult_notes?.findIndex((note) => note.id === updatedNote.id) ?? -1;

      if (!cached || noteIndex === -1) {
        // Saved row not cached: cancel any list fetch (it may predate this save;
        // invalidate alone joins a fetch that has no data yet), then refetch.
        void queryClient
          .cancelQueries({ queryKey: consultNotesKey })
          .then(() => queryClient.invalidateQueries({ queryKey: consultNotesKey }));
        return;
      }

      queryClient.setQueryData<GetConsultNotesResponse>(
        consultNotesKey,
        produce(cached, (draft) => {
          draft.consult_notes[noteIndex] = updatedNote;
        })
      );
    },
    onError: (error, { sessionId }, context) => {
      // sanitizeError, not the raw error: an AxiosError's toJSON serialises
      // `config.data` — here the consult note itself — and the Datadog
      // transport scrubs credentials, not PHI.
      logger.error('Failed to sync note to server', {
        sessionId,
        // Same dispatch-time snapshot the ports carry; falls back to the
        // current options only when `onMutate` itself threw, so no context
        // was ever built.
        contentType: context?.outcome?.contentType ?? resolvedContentType,
        editorType: context?.outcome?.editorType ?? options.editorType,
        error: sanitizeError(error),
      });
      if (context?.previousSession) {
        queryClient.setQueryData<QuerySessionResponse>(
          [getSessionKey, { sessionId }],
          context.previousSession
        );
      }

      options.onSyncError?.(error);
    },
    // Deliberately no onSettled invalidation: refetching the session after
    // every save echoes the server's copy back over the characters the
    // clinician has typed since the request left — the same clobber
    // `suppressContentSyncEchoWhileFocused` exists to hold off.
  });
}
